import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Clock, RefreshCw, Filter, User, Settings, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuditLogsPage() {
  const { adminKey } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: "", target: "" });

  useEffect(() => {
    fetchLogs();
  }, [adminKey, filter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/admin/audit-logs?limit=200`;
      if (filter.action) url += `&action=${filter.action}`;
      if (filter.target) url += `&target_id=${filter.target}`;
      
      const res = await fetch(url, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes("suspend")) return <AlertTriangle className="w-4 h-4 text-red-600" />;
    if (action.includes("activate")) return <User className="w-4 h-4 text-green-600" />;
    if (action.includes("grant")) return <Clock className="w-4 h-4 text-yellow-700" />;
    return <Settings className="w-4 h-4 text-gray-500" />;
  };

  const getActionStyle = (action) => {
    if (action.includes("suspend")) return "bg-red-900/30 border-red-200";
    if (action.includes("activate")) return "bg-emerald-900/30 border-green-200";
    if (action.includes("grant") || action.includes("waive")) return "bg-amber-900/30 border-yellow-200";
    if (action.includes("resolve")) return "bg-blue-900/30 border-blue-200";
    return "bg-gray-50/50 border-gray-300";
  };

  const uniqueActions = [...new Set(logs.map(l => l.action))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Audit Logs</h1>
          <p className="text-gray-500 text-sm mt-1">Track all administrative actions</p>
        </div>
        <button 
          onClick={fetchLogs}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select 
            value={filter.action}
            onChange={(e) => setFilter(f => ({ ...f, action: e.target.value }))}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800"
          >
            <option value="">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>{action.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
        <input
          type="text"
          placeholder="Filter by target ID..."
          value={filter.target}
          onChange={(e) => setFilter(f => ({ ...f, target: e.target.value }))}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 w-64"
        />
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-gray-800 font-semibold">Activity Log ({logs.length})</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No audit logs found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
            {logs.map((log, idx) => (
              <div key={log.id || idx} className={`p-4 border-l-4 ${getActionStyle(log.action)}`}>
                <div className="flex items-start gap-3">
                  {getActionIcon(log.action)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-800 font-medium capitalize">
                        {log.action.replace(/_/g, " ")}
                      </span>
                      <span className="text-gray-500">on</span>
                      <span className="text-gray-600 font-mono text-sm bg-gray-50 px-2 py-0.5 rounded">
                        {log.target_type}/{log.target_id?.slice(0, 12)}...
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1">{log.reason}</p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        {Object.entries(log.details).map(([k, v]) => (
                          <span key={k} className="mr-3">
                            <span className="text-gray-400">{k}:</span> {String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-gray-400 text-xs mt-2">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLogsPage;
